import { Feature, Point } from 'geojson';

import type { IlotAnalysisType, IlotPointFeature, IlotSample } from '../types/ilot';

const CARBON_RADIUS = '50%';
const FIELD_SAMPLE_RADIUS = '25%';
const CARBON_COLOR = '#40c057';
const FIELD_SAMPLE_COLOR = '#228be6';
export const FIELDWORK_LOCKED_COLOR = '#adb5bd';
export const DEFERRED_ORGANIC_COLOR = '#000000';

export interface FlattenedIlotPointProperties {
  zone: number;
  code: string;
  status: string;
  field_name: string;
  analysis_type: IlotAnalysisType;
  laboratory_name?: string | null;
  sachet_id?: string | null;
  stoniness?: string | null;
  stoniness_image?: string | null;
  is_sampled?: boolean;
  is_impossible?: boolean;
  impossible_title?: string | null;
  impossible_content?: string | null;
  impossible_image?: string | null;
  impossible_at?: string | null;
  sample: number;
  label: string;
  radius: string;
  color: string;
  sampleId: number;
  fieldwork_allowed: boolean;
  organic_status: string | null;
}

export type FlattenedIlotPointFeature = Feature<
  Point,
  FlattenedIlotPointProperties
>;

function pointDisplayStyle(
  analysisType: IlotAnalysisType,
  fieldworkAllowed: boolean,
  organicStatus: string | null,
) {
  if (!fieldworkAllowed) {
    return {
      radius: CARBON_RADIUS,
      color:
        organicStatus === 'deferred'
          ? DEFERRED_ORGANIC_COLOR
          : FIELDWORK_LOCKED_COLOR,
    };
  }
  if (analysisType === 'carbon') {
    return { radius: CARBON_RADIUS, color: CARBON_COLOR };
  }
  return { radius: FIELD_SAMPLE_RADIUS, color: FIELD_SAMPLE_COLOR };
}

function pointKey(sampleId: number, feature: IlotPointFeature): string {
  return `${sampleId}:${feature.id ?? ''}:${feature.properties.zone}`;
}

function isGranulo(feature: IlotPointFeature): boolean {
  return feature.properties.analysis_type === 'granulo';
}

/**
 * Map label for one ilot.
 * Every granulo point is GR1, GR2, … ordered by sample zone, then point zone.
 * Carbon keeps `{sample}.{zone}` so the pin matches the sample code suffix.
 */
export function flattenIlotSamples(
  samples: IlotSample[],
): FlattenedIlotPointFeature[] {
  const granuloLabelByKey = new Map<string, string>();
  samples
    .flatMap((sample) =>
      sample.points.features
        .filter(isGranulo)
        .map((feature) => ({
          key: pointKey(sample.id, feature),
          sampleZone: sample.zone,
          zone: feature.properties.zone,
          id: Number(feature.id ?? 0),
        })),
    )
    .sort(
      (a, b) =>
        a.sampleZone - b.sampleZone || a.zone - b.zone || a.id - b.id,
    )
    .forEach((row, index) => {
      granuloLabelByKey.set(row.key, `GR${index + 1}`);
    });

  return samples.flatMap((sample) =>
    sample.points.features.map((feature: IlotPointFeature) => {
      const fieldworkAllowed = feature.properties.fieldwork_allowed === true;
      const organicStatus = feature.properties.organic_status ?? null;
      const label =
        granuloLabelByKey.get(pointKey(sample.id, feature)) ??
        `${sample.zone}.${feature.properties.zone}`;
      return {
        ...feature,
        properties: {
          ...feature.properties,
          label,
          ...pointDisplayStyle(
            feature.properties.analysis_type,
            fieldworkAllowed,
            organicStatus,
          ),
          sampleId: sample.id,
          fieldwork_allowed: fieldworkAllowed,
          organic_status: feature.properties.organic_status ?? null,
        },
      };
    }),
  );
}
